// A chart drawn in the custom overworld tileset (docs/70) on one canvas: each cell's ground, the
// edges where grounds meet (`groundEdges`), its shore pieces, its road or bridge, then the features. The cell's hash picks among a tile's
// variants (unless the cell pins one), so a valley always looks the same. Drawn at 24 px and scaled
// by CSS at a whole number, like `MapCanvas`. A tile with no art yet shows as a magenta cell.
// `cast` lays the features' silhouettes away from the sun, as `MapCanvas` does for the Oryx props.
// `lights` draws only the lit pixels (docs/132 OP17), for a second canvas laid over the night light.
// The water moves (docs/70 CO10): animated tiles cycle their frames every `FRAME_MS`, and a liquids layer above
// the art carries glints downstream and rain rings, as `MapCanvas` does for the Oryx map; both hold still under
// reduce motion, and the frames stop when the water is iced.
import { useEffect, useMemo, useRef, useState } from 'react';
import { customChart, LAYER_SALT, pieceVariant, type CustomCell, type CustomChart, type Piece } from './customChart';
import { FRAME_CYCLE, FRAME_MS, frameAt, keepLit, tileKind, type ArtSeason, type Biome, type TimeOfDay } from './customTiles';
import { edgeMask, edgesAt, type EdgePiece } from './groundEdges';
import { customLiquids, drawMapLiquids, spawnRings, type LiquidLook, type MapLiquidState, type MapWeather } from './mapLiquids';
import { useSettings } from '../../../core/stores/settings';
import { loadCustomArt, type CustomArt } from './customArt';
import type { Overworld } from './overworld';
import { regionFields, shadeRegions, type RegionFields } from './regionLook';
import './map.css';

const CELL = 24;
/** The chart is painted on canvases kept in memory (thousands of small draws, and each edge drawn through a scratch
 *  canvas, are cheap there and stall a GPU canvas on every one) and only the finished picture goes to the screen. */
const CPU: CanvasRenderingContext2DSettings = { willReadFrequently: true };

/**
 * The art a chart is drawn from: one folder's (a one-country chart), or on a map of regions (docs/185 PB13) each
 * biome's, every cell from its own (`CustomCell.biome`). A biome with no art here draws nothing (the lights layer
 * passes only the folders lit at that hour).
 */
export type ChartArt = CustomArt | { byBiome: Partial<Record<Biome, CustomArt>> };
const artOf = (art: ChartArt, c: CustomCell): CustomArt | undefined => ('byBiome' in art ? (c.biome ? art.byBiome[c.biome] : undefined) : art);

function drawPiece(ctx: CanvasRenderingContext2D, art: CustomArt, p: Piece | undefined, x: number, y: number, seed: number, layer: keyof typeof LAYER_SALT, missing = true, step = 0) {
  if (!p) return;
  const v = pieceVariant(art.variants(p.tile), p, x, y, seed, layer);
  const img = v === undefined ? undefined : art.image(p.tile, v, frameAt(art.frames?.(p.tile, v) ?? 1, step));
  if (img) ctx.drawImage(img, x * CELL, y * CELL);
  else if (missing) { ctx.fillStyle = 'rgba(255, 0, 200, 0.55)'; ctx.fillRect(x * CELL + 2, y * CELL + 2, CELL - 4, CELL - 4); }
}

/** Each edge mask as a canvas (white where the neighbour's ground shows), made once. */
const masks = new Map<string, HTMLCanvasElement>();
function maskCanvas(piece: EdgePiece, variant: number): HTMLCanvasElement {
  const key = `${piece}:${variant}`;
  let c = masks.get(key);
  if (!c) {
    c = document.createElement('canvas');
    c.width = c.height = CELL;
    const ctx = c.getContext('2d', CPU)!, img = ctx.createImageData(CELL, CELL), m = edgeMask(piece, variant);
    m.forEach((on, i) => { if (on) img.data.set([255, 255, 255, 255], i * 4); });
    ctx.putImageData(img, 0, 0);
    masks.set(key, c);
  }
  return c;
}

/** Lays the edges of the grounds round each cell on it (docs/70 CO9): each mask filled with the
 *  neighbouring cell's own ground tile, at the same place in the tile. */
function drawEdges(ctx: CanvasRenderingContext2D, arts: ChartArt, chart: CustomChart, step: number) {
  const tmp = document.createElement('canvas');
  tmp.width = tmp.height = CELL;
  const tc = tmp.getContext('2d', CPU);
  if (!tc) return;
  tc.imageSmoothingEnabled = false;
  const { w, h, seed, cells } = chart;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    for (const e of edgesAt(chart, x, y)) {
      // PB13: the neighbour's own ground from its own folder, across a border too
      const [fx, fy] = e.from, from = cells[fy * w + fx]!, ground = from.ground, art = artOf(arts, from);
      if (!art) continue;
      const v = pieceVariant(art.variants(ground.tile), ground, fx, fy, seed, 'ground');
      const img = v === undefined ? undefined : art.image(ground.tile, v, frameAt(art.frames?.(ground.tile, v) ?? 1, step));
      if (!img) continue;
      tc.globalCompositeOperation = 'copy';
      tc.drawImage(maskCanvas(e.piece, e.variant), 0, 0);
      tc.globalCompositeOperation = 'source-in';
      tc.drawImage(img, 0, 0);
      ctx.drawImage(tmp, x * CELL, y * CELL);
    }
  }
}

/** How each country's water catches the light (CO10): its glints and rain rings, tinted to its own water. */
const LIQUID_LOOK: Partial<Record<Biome, LiquidLook>> = {
  wetland: { glint: '214,230,222', ring: '200,222,212' },
  jungle: { glint: '228,240,226', ring: '214,234,222' },
  desert: { glint: '236,252,250', ring: '214,244,244' },
  // the black lakes catch the glow: blue-green glints, dim rings
  underdark: { glint: '150,240,215', ring: '110,180,170' },
  // the Ashlands' water is lava: hot orange glints, ember-red rings where the black rain hisses on it
  ashlands: { glint: '255,190,110', ring: '255,120,60' },
};

/** The whole chart at animation step `step` on `ctx`: ground, edges, overlays, paths, then the features (and their
 *  shadows), or only the lit pixels for `lights`. `casters` is a scratch canvas for the shadows. On a map of regions
 *  (PB13) nothing under the Underdark's rock casts a sun's shadow, and `fields` veils the locked regions and sinks
 *  the Underdark (`regionLook.ts`). */
function paintChart(ctx: CanvasRenderingContext2D, arts: ChartArt, chart: CustomChart, step: number, lights: boolean, cast: { dx: number; dy: number; alpha: number } | undefined, casters: { current?: HTMLCanvasElement }, fields?: RegionFields) {
  const { w, h, seed, cells } = chart;
  const piece = (c: CustomCell, p: Piece | undefined, i: number, layer: keyof typeof LAYER_SALT, missing: boolean, frame = 0, to = ctx) => {
    const art = artOf(arts, c);
    if (art) drawPiece(to, art, p, i % w, Math.floor(i / w), seed, layer, missing, frame);
  };
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, w * CELL, h * CELL);
  cells.forEach((c, i) => piece(c, c.ground, i, 'ground', !lights, step));
  if (!lights) drawEdges(ctx, arts, chart, step);
  cells.forEach((c, i) => {
    c.overlays.forEach((o) => piece(c, o, i, 'overlay', !lights, step));
    piece(c, c.path, i, 'path', !lights);
  });
  if (lights) {
    cells.forEach((c, i) => piece(c, c.feature, i, 'feature', false));
    const px = ctx.getImageData(0, 0, w * CELL, h * CELL);
    keepLit(px.data);
    if (fields) shadeRegions(px.data, w, h, fields, true);
    ctx.putImageData(px, 0, 0);
    return;
  }
  if (cast && cast.alpha > 0 && (cast.dx || cast.dy)) {
    const off = (casters.current ??= document.createElement('canvas'));
    off.width = w * CELL; off.height = h * CELL;
    const oc = off.getContext('2d', CPU);
    if (oc) {
      cells.forEach((c, i) => { if (c.feature && !tileKind(c.feature.tile)?.flat && c.biome !== 'underdark') piece(c, c.feature, i, 'feature', false, 0, oc); });
      oc.globalCompositeOperation = 'source-in';
      oc.fillStyle = 'rgb(14, 16, 38)';
      oc.fillRect(0, 0, off.width, off.height);
      ctx.globalAlpha = cast.alpha;
      ctx.drawImage(off, cast.dx, cast.dy);
      ctx.globalAlpha = 1;
    }
  }
  cells.forEach((c, i) => piece(c, c.feature, i, 'feature', true));
  if (fields) {
    const px = ctx.getImageData(0, 0, w * CELL, h * CELL);
    shadeRegions(px.data, w, h, fields, false);
    ctx.putImageData(px, 0, 0);
  }
}

export function CustomMapCanvas({ chart, art, scale = 2, cast, lights = false, version = 0, className = '', biome, animate = true, ice = 0, weather, fields }: {
  chart: CustomChart; art: ChartArt | undefined; scale?: number;
  /** docs/185 PB13: the locked regions' veil and the Underdark's sinking, on a map of regions. */
  fields?: RegionFields;
  /** The country, for how its water looks (CO10); without it the water takes the default look. */
  biome?: Biome;
  /** Whether the water moves (off when it is iced); reduce motion holds it still regardless. */
  animate?: boolean; ice?: number; weather?: Omit<MapWeather, 'ice'>;
  /** The sun's shadows: features cast their silhouette `dx`, `dy` px away at `alpha`; flat tiles cast none. */
  cast?: { dx: number; dy: number; alpha: number };
  /** Draw only the lit pixels (lit windows, doors and fires at night), with no shadows or missing cells. */
  lights?: boolean;
  /** Bump to redraw when the art changed in place (the Studio, as a tile is drawn). */
  version?: number;
  className?: string;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const casters = useRef<HTMLCanvasElement>(undefined);
  const { w, h, seed, cells } = chart;
  // the frames' clock: only when something on the chart has frames, the water isn't iced and motion is allowed
  const still = useSettings((s) => s.reduceMotion);
  const moving = useMemo(() => !!art && cells.some((c, i) => [c.ground, ...c.overlays].some((p) => {
    const a = artOf(art, c);
    if (!a?.frames) return false;
    const v = pieceVariant(a.variants(p.tile), p, i % w, Math.floor(i / w), seed, p === c.ground ? 'ground' : 'overlay');
    return v !== undefined && a.frames(p.tile, v) > 1;
  })), [art, cells, w, seed]);
  const [step, setStep] = useState(0);
  const ticking = moving && animate && ice < 0.5 && !still && !lights;
  useEffect(() => {
    if (!ticking) { setStep(0); return; }
    const t = window.setInterval(() => setStep((s) => s + 1), FRAME_MS);
    return () => window.clearInterval(t);
  }, [ticking]);
  // The water's frames come round every FRAME_CYCLE steps, so each is painted once (the ones not yet shown when the
  // page is idle) and a tick only copies one onto the canvas: repainting the whole chart every tick stalled the
  // camera mid-zoom.
  const frames = useMemo(() => new Map<number, HTMLCanvasElement>(), [art, chart, cast, lights, version, fields]);
  useEffect(() => {
    const ctx = ref.current?.getContext('2d');
    if (!ctx || !art) return;
    const frame = (k: number) => {
      let c = frames.get(k);
      if (!c) {
        c = document.createElement('canvas');
        c.width = w * CELL; c.height = h * CELL;
        const fc = c.getContext('2d', CPU);
        // the paint's cost shows in a profile's user timings (`fk-chart-paint`, docs/185 PB13 measured it)
        const t0 = performance.now();
        if (fc) paintChart(fc, art, chart, k, lights, cast, casters, fields);
        performance.measure(lights ? 'fk-chart-paint-lights' : 'fk-chart-paint', { start: t0 });
        frames.set(k, c);
      }
      return c;
    };
    ctx.globalCompositeOperation = 'copy';
    ctx.drawImage(frame(step % FRAME_CYCLE), 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    if (!ticking) return;
    const idle = window.requestIdleCallback ?? ((f: () => void) => window.setTimeout(f, 200));
    const cancel = window.cancelIdleCallback ?? window.clearTimeout;
    let job = 0;
    const ahead = () => {
      const k = Array.from({ length: FRAME_CYCLE }, (_, i) => i).find((i) => !frames.has(i));
      if (k === undefined) return;
      frame(k);
      job = idle(ahead);
    };
    job = idle(ahead);
    return () => cancel(job);
  }, [art, chart, w, h, cast, lights, ticking, frames, step, fields]);
  const canvas = <canvas ref={ref} className={`fk-mapcanvas ${lights ? 'fk-ow-lights' : ''} ${className}`} width={w * CELL} height={h * CELL} aria-hidden="true" data-ready={art ? 'true' : 'false'} data-custom-art="true" data-step={step} style={{ width: w * CELL * scale, height: h * CELL * scale }} />;
  if (lights || !art) return canvas;
  return (
    <>
      {canvas}
      <CustomLiquids chart={chart} biome={biome} ice={ice} weather={weather} scale={scale} className={className} still={still} fields={fields} />
    </>
  );
}

/** The moving liquids over the custom art (CO10), as `MapCanvas`'s over the Oryx map: ~12 frames a second, one
 *  still frame under reduce motion. The banks' foam is the art's own frames, so the layer laps none. On a map of
 *  regions (PB13) each region's water catches its own light, no rain rings the Underdark's black lakes, and a locked
 *  region's water lies still under its veil. */
function CustomLiquids({ chart, biome, ice, weather, scale, className, still, fields }: {
  chart: CustomChart; biome: Biome | undefined; ice: number; weather?: Omit<MapWeather, 'ice'>; scale: number; className: string; still: boolean; fields?: RegionFields;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const { w, h } = chart;
  const groups = useMemo(() => {
    const regions = chart.cells.some((c) => c.biome);
    const all = customLiquids(chart, regions ? (x, y) => chart.cells[y * w + x]!.biome === 'desert' : biome === 'desert');
    if (!regions) return [{ biome, cells: all }];
    const by = new Map<Biome, typeof all>();
    for (const c of all) {
      if (fields && fields.veil[c.y * w + c.x]! > 0.5) continue;
      const b = chart.cells[c.y * w + c.x]!.biome!;
      by.set(b, [...(by.get(b) ?? []), c]);
    }
    return [...by].map(([b, cells]) => ({ biome: b as Biome | undefined, cells }));
  }, [chart, biome, w, fields]);
  const count = groups.reduce((n, g) => n + g.cells.length, 0);
  const wx = useRef<MapWeather>({ precip: undefined, amount: 0, ice });
  wx.current = { precip: weather?.precip, amount: weather?.amount ?? 0, under: weather?.under, ice };
  useEffect(() => {
    const ctx = ref.current?.getContext('2d');
    if (!ctx || count === 0) return;
    const looks = groups.map((g) => ({ ...(g.biome ? LIQUID_LOOK[g.biome] : undefined), foam: false }) as LiquidLook);
    const states: MapLiquidState[] = groups.map(() => ({ rings: [], last: 0 }));
    // no weather under the rock
    const sky = (g: (typeof groups)[number]): MapWeather => (g.biome === 'underdark' && groups.length > 1 ? { precip: undefined, amount: 0, ice: wx.current.ice } : wx.current);
    const paint = (t: number, spawn: boolean) => groups.forEach((g, i) => {
      if (spawn) spawnRings(states[i]!, g.cells, sky(g), t, w);
      drawMapLiquids(ctx, g.cells, states[i]!, sky(g), t, looks[i], i === 0);
    });
    if (still) { paint(12.5, false); return; }
    let raf = 0, last = -1;
    const frame = (ms: number) => {
      raf = requestAnimationFrame(frame);
      if (last >= 0 && ms - last < 80) return;
      last = ms;
      paint(ms / 1000, true);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [groups, count, still, w]);
  if (count === 0) return null;
  return <canvas ref={ref} className={`fk-mapcanvas fk-mapcanvas--liquid ${className}`} width={w * CELL} height={h * CELL} aria-hidden="true" data-testid="map-liquids" data-cells={count} style={{ width: w * CELL * scale, height: h * CELL * scale }} />;
}

/**
 * The World chart in the custom art (docs/70, docs/143): the valley read into
 * the custom tiles, drawn from the folder for the World's country, season and time of day. On a map of regions
 * (docs/185 PB13) every region's folder is loaded (`times` gives a region its own time of day: the Underdark's glow
 * keeps its own hours), and the locked regions are veiled.
 */
export function CustomOverworldMap({ ow, biome, season, time, times, cast, lights, scale = 2, animate = true, ice = 0, weather }: {
  ow: Overworld; biome: Biome; season: ArtSeason; time: TimeOfDay; cast?: { dx: number; dy: number; alpha: number }; lights?: boolean; scale?: number;
  animate?: boolean; ice?: number; weather?: Omit<MapWeather, 'ice'>;
  /** PB13: a region's own time of day, where it differs from `time`; for `lights`, only the biomes listed light up. */
  times?: Partial<Record<Biome, TimeOfDay>>;
}) {
  const chart = useMemo(() => customChart(ow, biome, season), [ow, biome, season]);
  const fields = useMemo(() => regionFields(ow), [ow]);
  const biomes = useMemo(() => (ow.regions ? [...new Set(ow.regions.map((r) => r.biome))] : undefined), [ow]);
  const timeKey = biomes?.map((b) => `${b}:${times?.[b] ?? time}`).join(',') ?? '';
  const [art, setArt] = useState<ChartArt>();
  useEffect(() => {
    let live = true;
    if (!biomes) void loadCustomArt(biome, season, time).then((a) => { if (live) setArt(a); });
    else {
      // the lights layer draws only the folders whose own hour is night
      const shown = lights && times ? biomes.filter((b) => times[b] === 'night') : biomes;
      void Promise.all(shown.map((b) => loadCustomArt(b, season, times?.[b] ?? time).then((a) => [b, a] as const))).then((all) => {
        if (live) setArt({ byBiome: Object.fromEntries(all.filter(([, a]) => a)) as Partial<Record<Biome, CustomArt>> });
      });
    }
    return () => { live = false; };
  }, [biome, season, time, timeKey, lights]);
  return <CustomMapCanvas chart={chart} art={art} cast={lights ? undefined : cast} lights={lights} scale={scale} biome={biome} animate={animate} ice={ice} weather={weather} fields={fields} />;
}
